import React from 'react';

import { gql, useQuery } from '@apollo/client';
import { Alert, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { find, isEmpty, map, sortBy } from 'lodash';
import { useParams } from 'react-router-dom';
import { ContainerFlexHeight, LoadingSpinner } from '../../../../components';
import { i18n } from '../../../../i18n/i18n';
import JobDetailSummary from '../../[id]/job_detail_summary';
import { JobCardItem } from '../../page/job_card_item';
import { calJobsPositionCountMap } from '../../../../utils/libs';

const getPeriodJobsGql = gql(`
  query periodJobs($periodJobId: ID) {
    periodJobs(id: $periodJobId) {
      id
      periodStartAt
      periodEndAt
      requiredDayCount
      jobs {
        id
        jobType
        status
        numberOfPosition
        scheduleDate
        jobStartAt
        jobEndAt
        expiredAt
        isImmediateAccept
        jobApplicants {
          id
          status
        }
        jobHirings {
          id
          status
        }
        periodJob {
          id
        }
      }
    }
  }
`);

const PeriodJobDetailPage = () => {
  const { id: periodJobId } = useParams();

  const { data: periodJobsData, loading: periodJobsLoading } = useQuery(getPeriodJobsGql, {
    fetchPolicy: 'cache-and-network',
    variables: { periodJobId },
    skip: !periodJobId,
  });

  const periodJob = React.useMemo(
    () => find(periodJobsData?.periodJobs, (_periodJob) => _periodJob?.id === periodJobId),
    [periodJobsData?.periodJobs, periodJobId],
  );
  const jobs = React.useMemo(() => sortBy(periodJob?.jobs, (job) => dayjs(job.jobStartAt)), [periodJob?.jobs]);
  const jobsPositionCountMap = React.useMemo(() => calJobsPositionCountMap(jobs), [jobs]);

  if (periodJobsLoading) {
    return <LoadingSpinner />;
  }

  if (isEmpty(periodJob)) {
    return (
      <Stack flex={1} flexGrow={1} justifyContent="center" alignItems="center">
        <Alert severity="warning">{i18n.t('general.empty')}</Alert>
      </Stack>
    );
  }

  return (
    <ContainerFlexHeight maxWidth="md">
      <Stack height="100%" gap={1}>
        <Stack>
          <JobDetailSummary jobId={periodJob.jobs?.[0].id} disableNumberOfPosition />
        </Stack>

        <Stack flex={1} sx={{ overflowY: 'scroll' }}>
          <Stack gap={1} padding={2}>
            {map(periodJob.jobs, (job) => (
              <JobCardItem
                key={job.id}
                job={job}
                showScheduleDate
                disableEndedJob
                navigateTo={`/jobs/${job.id}`}
                applicantCount={jobsPositionCountMap[job.id].applicantCount}
                hiringCount={jobsPositionCountMap[job.id].hiringCount}
              />
            ))}
          </Stack>
        </Stack>
      </Stack>
    </ContainerFlexHeight>
  );
};

export default PeriodJobDetailPage;
